"use client"

import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { ChevronLeft, ChevronRight, MoreHorizontal, Search, SlidersHorizontal } from "lucide-react"
import { Badge } from "../ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { adminDashboard } from '../../services/api'

// Interface pour les utilisateurs
interface User {
  id: number
  fullName: string
  email: string
  role: 'admin' | 'user' | 'formateur'
  photo: string | null
  createdAt: string
  banned: boolean
  approvedFormationCount: number
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user' | 'formateur'>('all');
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const roleLabel: Record<'admin' | 'user' | 'formateur', string> = {
    admin: 'Admin',
    user: 'Étudiant',
    formateur: 'Formateur',
  };

  // Charger les utilisateurs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true)
        const response = await adminDashboard.getAllUsers()
        if (response.data.success) {
          setUsers(response.data.data)
        }
      } catch (err) {
        console.error('Erreur lors du chargement des utilisateurs:', err)
        setError('Erreur lors du chargement des utilisateurs')
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [])

  // Fonction pour recharger les utilisateurs
  const refreshUsers = async () => {
    try {
      const response = await adminDashboard.getAllUsers()
      if (response.data.success) {
        setUsers(response.data.data)
      }
    } catch (err) {
      console.error('Erreur lors du rechargement des utilisateurs:', err)
    }
  }

  // Fonction pour formater la date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)

      if (isNaN(date.getTime())) {
        return dateString
      }

      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()

      return `${day}/${month}/${year}`
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error)
      return dateString
    }
  }

  // Fonction pour gérer les actions (ban/unban/delete)
  const handleUserAction = async (userId: number, action: 'ban' | 'unban' | 'delete') => {
    try {
      setProcessingIds(prev => new Set(prev).add(userId))

      await adminDashboard.updateUserAction(userId, action)

      // Recharger la liste des utilisateurs après chaque action
      await refreshUsers()

    } catch (err) {
      console.error(`Erreur lors de l'action ${action} sur l'utilisateur:`, err)
      setError(`Erreur lors de l'action ${action} sur l'utilisateur`)
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // Fonction pour changer le rôle d'un utilisateur
  const handleRoleChange = async (userId: number, newRole: 'admin' | 'user' | 'formateur') => {
    try {
      setProcessingIds(prev => new Set(prev).add(userId))

      await adminDashboard.changeUserRole(userId, newRole)

      // Recharger la liste des utilisateurs après le changement de rôle
      await refreshUsers()

    } catch (err) {
      console.error('Erreur lors du changement de rôle:', err)
      setError('Erreur lors du changement de rôle')
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // Obtenir les initiales pour l'avatar
  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Filtrer les utilisateurs
  const filteredUsers = users.filter((user) => {
    // 1. Filtrer par terme de recherche
    const matchesSearch =
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())

    // 2. Filtrer par rôle
    const matchesRole =
      !roleFilter || roleFilter === 'all' ? true : user.role === roleFilter

    return matchesSearch && matchesRole
  })

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage)
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, roleFilter, itemsPerPage])

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-500">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold tracking-tight">Gestion des utilisateurs</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Gérez les comptes utilisateurs, leurs rôles et leurs accès.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Liste des utilisateurs</CardTitle>
            <CardDescription>{filteredUsers.length} utilisateurs trouvés</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <Input
                    type="search"
                    placeholder="Rechercher..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Select
                    value={roleFilter}
                    onValueChange={(value) =>
                      setRoleFilter(value as 'all' | 'admin' | 'user' | 'formateur')
                    }
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tous les rôles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les rôles</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">Étudiant</SelectItem>
                      <SelectItem value="formateur">Formateur</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon">
                    <SlidersHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">Afficher</p>
                <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder={itemsPerPage} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">par page</p>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Date d'inscription</TableHead>
                    <TableHead>Formations</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={user.photo || undefined} alt={user.fullName} />
                              <AvatarFallback>{getInitials(user.fullName)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {user.fullName}
                                {user.banned && (
                                  <Badge variant="destructive" className="text-xs">
                                    Banni
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="bg-gray-50 text-gray-700 border-gray-200"
                          >
                            {{
                              admin: "Admin",
                              user: "Étudiant",
                              formateur: "Formateur",
                            }[user.role] ?? "Étudiant"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                        <TableCell>{user.approvedFormationCount}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={processingIds.has(user.id)}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <a href={`/admin/users/${user.id}`}>
                                <DropdownMenuItem>Voir le profil</DropdownMenuItem>
                              </a>
                              <DropdownMenuSeparator />
                              
                              {/* Section Changer le rôle */}
                              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground px-2 py-1.5">
                                Changer le rôle
                              </DropdownMenuLabel>
                              <div className="px-2 py-1">
                                <Select
                                  value={user.role}
                                  onValueChange={(newRole: 'admin' | 'user' | 'formateur') => {
                                    if (newRole !== user.role) {
                                      handleRoleChange(user.id, newRole)
                                    }
                                  }}
                                  disabled={processingIds.has(user.id)}
                                >
                                  <SelectTrigger className="w-full h-8">
                                    <SelectValue placeholder="Définir comme" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">Étudiant</SelectItem>
                                    <SelectItem value="formateur">Formateur</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <DropdownMenuSeparator />
                              
                              {/* Actions de modération */}
                              {!user.banned ? (
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleUserAction(user.id, 'ban')}
                                  disabled={processingIds.has(user.id)}
                                >
                                  {processingIds.has(user.id) ? 'Traitement...' : 'Banner'}
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  className="text-green-600"
                                  onClick={() => handleUserAction(user.id, 'unban')}
                                  disabled={processingIds.has(user.id)}
                                >
                                  {processingIds.has(user.id) ? 'Traitement...' : 'Débanner'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleUserAction(user.id, 'delete')}
                                disabled={processingIds.has(user.id)}
                              >
                                {processingIds.has(user.id) ? 'Traitement...' : 'Supprimer'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        Aucun utilisateur trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
                  {Math.min(currentPage * itemsPerPage, filteredUsers.length)} sur {filteredUsers.length} utilisateurs
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-sm">
                    Page {currentPage} sur {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
